'use client';

import React, { useState, useEffect } from 'react';
import { Modal } from '../Modal/Modal';
import { MaintenanceWindowForm, type MaintenanceWindowFormData, type MaintenanceWindowFormErrors } from './MaintenanceWindowForm';
import { i18n } from '@/config/i18n';
import type { MaintenanceWindow } from '@/services/siteService';

export interface MaintenanceWindowEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  maintenanceWindow: MaintenanceWindow | null;
  onUpdate: (data: {
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
    comments: string;
  }) => Promise<void>;
}

export const MaintenanceWindowEditModal: React.FC<MaintenanceWindowEditModalProps> = ({
  isOpen,
  onClose,
  maintenanceWindow,
  onUpdate,
}) => {
  const [formData, setFormData] = useState<MaintenanceWindowFormData>({
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    comments: '',
  });

  const [errors, setErrors] = useState<MaintenanceWindowFormErrors>({});
  const [loading, setLoading] = useState(false);

  // Populate form when maintenance window changes
  useEffect(() => {
    if (maintenanceWindow && isOpen) {
      // Parse ISO timestamps to date/time inputs
      if (maintenanceWindow.start_time) {
        const startDateObj = new Date(maintenanceWindow.start_time);
        setFormData(prev => ({
          ...prev,
          startDate: startDateObj.toISOString().split('T')[0],
          startTime: startDateObj.toTimeString().slice(0, 5),
        }));
      }
      if (maintenanceWindow.end_time) {
        const endDateObj = new Date(maintenanceWindow.end_time);
        setFormData(prev => ({
          ...prev,
          endDate: endDateObj.toISOString().split('T')[0],
          endTime: endDateObj.toTimeString().slice(0, 5),
        }));
      }
      
      setFormData(prev => ({
        ...prev,
        comments: maintenanceWindow.comments || '',
      }));
    } else if (!maintenanceWindow && isOpen) {
      // Reset form when modal opens without maintenance window
      setFormData({
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        comments: '',
      });
    }
    setErrors({});
  }, [maintenanceWindow, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: MaintenanceWindowFormErrors = {};

    if (!formData.startDate) {
      newErrors.startDate = i18n.monitors.detail.maintenanceWindow.validation.startDateRequired;
    }
    if (!formData.startTime) {
      newErrors.startTime = i18n.monitors.detail.maintenanceWindow.validation.startTimeRequired;
    }
    if (!formData.endDate) {
      newErrors.endDate = i18n.monitors.detail.maintenanceWindow.validation.endDateRequired;
    }
    if (!formData.endTime) {
      newErrors.endTime = i18n.monitors.detail.maintenanceWindow.validation.endTimeRequired;
    }

    // Skip time validation for active or completed maintenance windows
    const isActiveOrCompleted = maintenanceWindow?.status === 'active' || maintenanceWindow?.status === 'completed' || maintenanceWindow?.is_active === true;

    // Validate that start time is not in the past (if start date is today)
    // Skip this validation for active or completed maintenance windows
    if (!isActiveOrCompleted && formData.startDate && formData.startTime) {
      const today = new Date().toISOString().split('T')[0];
      if (formData.startDate === today) {
        const now = new Date();
        const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
        const startDateTime = new Date(`${formData.startDate}T${formData.startTime}`);
        const nowDateTime = new Date(`${formData.startDate}T${currentTime}`);
        
        if (startDateTime < nowDateTime) {
          newErrors.startTime = i18n.monitors.detail.maintenanceWindow.validation.startTimeNotPast || 'Start time cannot be in the past';
        }
      }
    }

    // Validate that end time is not in the past (if end date is today)
    // Skip this validation for active or completed maintenance windows
    if (!isActiveOrCompleted && formData.endDate && formData.endTime) {
      const today = new Date().toISOString().split('T')[0];
      if (formData.endDate === today) {
        const now = new Date();
        const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
        const endDateTime = new Date(`${formData.endDate}T${formData.endTime}`);
        const nowDateTime = new Date(`${formData.endDate}T${currentTime}`);
        
        if (endDateTime < nowDateTime) {
          newErrors.endTime = i18n.monitors.detail.maintenanceWindow.validation.endTimeNotPast || 'End time cannot be in the past';
        }
      }
    }

    // Validate end date/time is after start date/time
    // Skip this validation for active or completed maintenance windows
    if (!isActiveOrCompleted && formData.startDate && formData.endDate && formData.startTime && formData.endTime) {
      const startDateTime = new Date(`${formData.startDate}T${formData.startTime}`);
      const endDateTime = new Date(`${formData.endDate}T${formData.endTime}`);

      if (endDateTime <= startDateTime) {
        if (formData.startDate === formData.endDate) {
          newErrors.endTime = i18n.monitors.detail.maintenanceWindow.validation.endTimeAfterStart;
        } else {
          newErrors.endDate = i18n.monitors.detail.maintenanceWindow.validation.endDateAfterStart;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      await onUpdate(formData);
      onClose();
    } catch (error) {
      console.error('Failed to update maintenance window:', error);
      setErrors({ submit: i18n.monitors.detail.maintenanceWindow.messages.failedToUpdate });
    } finally {
      setLoading(false);
    }
  };

  const handleFormDataChange = (data: Partial<MaintenanceWindowFormData>) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  const handleErrorsChange = (newErrors: Partial<MaintenanceWindowFormErrors>) => {
    setErrors(prev => ({ ...prev, ...newErrors }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={i18n.monitors.detail.maintenanceWindow.buttons.edit || i18n.common.buttons.edit || 'Edit Maintenance Window'}
      maxWidth="800px"
      backdropBlur
    >
      <MaintenanceWindowForm
        formData={formData}
        errors={errors}
        loading={loading}
        onFormDataChange={handleFormDataChange}
        onErrorsChange={handleErrorsChange}
        onSubmit={handleSubmit}
        onCancel={onClose}
        submitButtonText={i18n.monitors.detail.maintenanceWindow.buttons.update || i18n.success.updated || 'Update'}
        disableTimingFields={maintenanceWindow?.status === 'active' || maintenanceWindow?.status === 'completed'}
      />
    </Modal>
  );
};

export default MaintenanceWindowEditModal;

